import React, { useState, useEffect, useMemo } from "react";
import ParticipantView from "../components/ParticipantView";
import { useMeeting, usePubSub } from "@videosdk.live/react-sdk";
import CharacterView from "../components/CharacterView";
import { logType, useJourney } from "../utils/common";
import { useAppContext } from "../AppContextDef";
import MainCard from "../components/MainCard";
import { useTranslation } from "react-i18next";
import useIsMobile from "../utils/useIsMobile";
import useIsTab from "../utils/useIsTab";
import imageSrc from "../repeat_audio_icon.png";
import DisagreeDialog from "../components/DisagreeDialog";
import usePubsubFunction from "../utils/usePubsubFunction";

const PolicyDetailsPage = ({
  handleAgree,
  handleDisagree,
  handleDisagreeAPI,
  currentPage,
  characterJoined,
  capturedImage,
  setCapturedImage,
  getScreenshotImage,
  abortSignal,
}) => {
  const {
    applicationNo,
    allDetails,
    journeyConfig,
    selectedLanguageCode,
    HeaderSectionHeight,
  } = useAppContext();
  const { end } = useMeeting();
  const { logJourney } = useJourney();
  const [agreed, setAgreed] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [isCharacterSpeaking, setIsCharacterSpeaking] = useState(false);
  const [inputValues, setInputValues] = useState([]);
  const [buttonSectionHeightRef, setButtonSectionHeightRef] = useState(null);
  const { publish: agreePublish } = usePubSub(`AGREE`);
  const { publish: disagreePublish } = usePubSub(`DISAGREE`);
  const pubsubPublishMethod = usePubsubFunction()


  const ButtonSectionHeight = useMemo(
    () => buttonSectionHeightRef?.offsetHeight,
    [buttonSectionHeightRef]
  );

  const { t, i18n } = useTranslation();
  let isMobile = useIsMobile();
  let isTab = useIsTab();

  let isMobileOrTab = isMobile || isTab;

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    changeLanguage(selectedLanguageCode);
  }, [selectedLanguageCode]);

  useEffect(() => {
    const productDetails =
      allDetails?.iciciApplicationDetails?.productDetails || [];
    const initialValues = productDetails.map((product) => ({
      planName: product.productName,
      lifeCoverSum: product.sumAssured,
      premiumAmount: product.premiumAmnt,
      frequency: product.premFrequency,
      premiumPayingTerm: product.premPayTerm,
      policyTerm: product.policyTerm,
    }));
    setInputValues(initialValues);
  }, [allDetails]);

  useEffect(() => {
    logJourney({
      type: logType.NAVIGATION,
      message: "Navigated to Policy Details Page",
    });
  }, []);

  const isEnabled = agreed && !isCharacterSpeaking && messageSent;
  const [showDisagreeDialog, setShowDisagreeDialog] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  let titleSpacing = inputValues.length > 1 ? 140 : 60;
  let desktopTitleSpacing = inputValues.length > 1 ? 160 : 80;
  let extraspace = inputValues.length === 1 && 40;

  let spacingHeight = isMobile
    ? HeaderSectionHeight + ButtonSectionHeight + 180 + titleSpacing + 14
    : isTab
      ? HeaderSectionHeight + ButtonSectionHeight + 290 + titleSpacing + 14
      : ButtonSectionHeight + desktopTitleSpacing + 40 + 36 + extraspace;

  let buttonCondition = !isCharacterSpeaking && messageSent;

  return (
    <div className="flex flex-col-reverse lg:flex-row flex-1 py-3 px-3 lg:py-9 lg:px-8 xl:px-14 gap-3 md:gap-4 lg:gap-5 xl:gap-16">
      <MainCard
        mainTitle={t(`Policy Details`)}
        FirstSection={
          <div>
            {inputValues.length > 1 && (
              <div className="mt-4 px-1 lg:px-0 mb-3 md:mb-0">
                <p className="text-[12px] lg:text-sm mb-0 text-gray-150">
                  {t("Total Premium Amount(Inclusive of all applicable taxes)")}
                </p>
                <p className="mt-1 text-base lg:text-lg font-semibold text-black-150">
                  {allDetails?.iciciApplicationDetails.totalPremiumAmnt}₹
                </p>
              </div>
            )}
            <div
              style={{
                // height: `calc(100vh - ${ spacingHeight }px)`,
                height: `${viewportHeight - spacingHeight}px`,
              }}
              className="overflow-y-auto scrollable-content scrollbar scrollbar-primary"
            >
              {inputValues.map((inputValue, index) => (
                <div key={index} className="mt-3 lg:mt-8 px-1 lg:px-0">
                  {inputValues.length !== 1 && (
                    <p
                      className={`block text-2xl lg:text-4xl ${index !== 0 && "mt-6"
                        } lg:mt-0 text-black-200 font-extrabold mb-6 lg:mb-14`}
                    >
                      {t(`Policy Details ${index + 1}`)}
                    </p>
                  )}
                  <div className="flex">
                    <div>
                      <p
                        htmlFor="planName"
                        className="text-[12px] lg:text-sm mb-0 text-gray-150"
                      >
                        {t("Plan Name")}
                      </p>
                      <p
                        id="planName"
                        className="mt-1 text-sm md:text-base lg:text-lg font-semibold text-black-150"
                      >
                        {inputValue.planName}
                      </p>
                    </div>
                  </div>
                  <div className="flex mt-3 lg:mt-4">
                    <div>
                      <p
                        htmlFor="lifeCover"
                        className="text-[12px] lg:text-sm mb-0 text-gray-150"
                      >
                        {t("Life CoverSum assured")}
                      </p>
                      <p
                        id="lifeCover"
                        className="mt-1 text-sm md:text-base lg:text-lg font-semibold text-black-150"
                      >
                        {inputValue.lifeCoverSum}₹
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-row lg:flex-col">
                    <div className="flex flex-col flex-1 mt-3 lg:mt-4">
                      <p
                        htmlFor="premiumAmount"
                        className="text-[12px] lg:text-sm mb-0 text-gray-150"
                      >
                        {t("Premium Amount(Inclusive of all applicable taxes)")}
                      </p>
                      <p
                        id="premiumAmount"
                        className="mt-1 text-sm md:text-base lg:text-lg font-semibold text-black-150"
                      >
                        {inputValue.premiumAmount}₹
                      </p>
                    </div>

                    <div className="flex flex-col flex-1 mt-3 lg:mt-4">
                      <p
                        htmlFor="frequency"
                        className="text-[12px] lg:text-sm mb-0 text-gray-150"
                      >
                        {t("Frequency")}
                      </p>
                      <p
                        id="frequency"
                        className="mt-1 text-sm md:text-base lg:text-lg font-semibold text-black-150"
                      >
                        {inputValue.frequency}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-row lg:flex-col">
                    <div className="flex flex-col flex-1 mt-3 lg:mt-4">
                      <p
                        htmlFor="premiumPayingTerm"
                        className="text-[12px] lg:text-sm mb-0 text-gray-150"
                      >
                        {t("Premium paying term")}
                      </p>
                      <p
                        id="premiumPayingTerm"
                        className="mt-1 text-sm md:text-base lg:text-lg font-semibold text-black-150"
                      >
                        {inputValue.premiumPayingTerm}
                        {inputValue.premiumPayingTerm === 1
                          ? " Year"
                          : inputValue.premiumPayingTerm > 1
                            ? " Years"
                            : ""}
                      </p>
                    </div>

                    <div className="flex flex-col flex-1 mt-3 lg:mt-4">
                      <p
                        htmlFor="policyTerm"
                        className="text-[12px] lg:text-sm mb-0 text-gray-150"
                      >
                        {t("Policy term")}
                      </p>
                      <p
                        id="policyTerm"
                        className="mt-1 text-sm md:text-base lg:text-lg font-semibold text-black-150"
                      >
                        {inputValue.policyTerm}
                        {inputValue.policyTerm === 1
                          ? " Year"
                          : inputValue.policyTerm > 1
                            ? " Years"
                            : ""}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* <div
              ref={setPolicyConfirmSectionHeightRef}
              className="mt-4 xl:mt-3 border-t border-darkGray-100 px-2 lg:px-0"
            >
              <p className="text-base lg:text-lg  italic text-black-150 mt-[10px]">
                {journeyConfig?.policyConfirmationText}
              </p>
            </div> */}
          </div>
        }
        SecondSection={
          <div
            ref={setButtonSectionHeightRef}
            className={`mt-3 md:mt-4 xl:mt-3 px-1 lg:px-0`}
          >
            <div
              // ref={setPolicyConfirmSectionHeightRef}
              className="border-t border-darkGray-100 max-h-14 lg:max-h-16 overflow-y-auto scrollbar scrollbar-primary"
            >
              <p className="text-xs md:text-base italic text-black-150 mt-[10px]">
                {journeyConfig?.policyConfirmationText}
              </p>
            </div>
            <div className="flex justify-between mt-3 md:mt-5">
              <div className="flex flex-1 text-sm md:text-base">
                <button
                  disabled={!isEnabled}
                  className={`disabled:bg-opacity-15 disabled:cursor-not-allowed bg-orange-150 text-white p-2 md:p-3 rounded-md w-full font-bold lg:max-w-max`}
                  onClick={() => {
                    handleAgree();
                    pubsubPublishMethod(agreePublish,
                      "agreePublish",
                      { persist: true },
                      {
                        isAgreeClick: true,
                        currentPage: "PolicyDetails",
                      },
                    );
                    logJourney({
                      type: logType.ACTION,
                      message: "Agree Button Clicked In Policy Details Page",
                    });
                  }}
                >
                  {t("I Agree")}
                </button>
                <button
                  className="bg-gray-100 text-black-150 p-2 md:p-3 rounded-md border border-darkGray-150 ml-3 w-full font-bold lg:max-w-max"
                  onClick={async () => {
                    setShowDisagreeDialog(true);
                  }}
                >
                  {t("Disagree")}
                </button>
              </div>
              <button
                className={`bg-blue-250 text-blue-150 disabled:cursor-not-allowed flex items-center justify-center text-sm md:text-base ml-3 lg:ml-0 p-2 md:p-3 rounded-md font-bold max-w-max`}
                disabled={!buttonCondition}
                onClick={() => {
                  setMessageSent(false);
                }}
              >
                <img src={imageSrc} alt="icon" className="w-6 h-6" />
              </button>
              <DisagreeDialog
                open={showDisagreeDialog}
                onCancel={() => {
                  setShowDisagreeDialog(false);
                }}
                onDisagree={async (disagreeReason) => {
                  pubsubPublishMethod(disagreePublish,
                    "disagreePublish",
                    { persist: true },
                    {
                      isDisagreeClick: true,
                      currentPage: "PolicyDetails",
                    },
                  );
                  await logJourney({
                    type: logType.ERROR,
                    message: "Disagree Button Clicked In Policy Details Page with reason: " + disagreeReason,
                  });
                  await handleDisagreeAPI(disagreeReason);
                  end();
                  handleDisagree();
                }}
              />
            </div>
          </div>
        }
      />

      <div
        className={`flex flex-col flex-1 ${isMobileOrTab && "sticky top-30 lg:top-[15%]"
          }`}
      >
        {isMobile ? (
          <div className="flex gap-3">
            <div className="flex flex-1 rounded-2xl p-2 items-center ">
              <div className="">
                <p className="text-sm lg:text-base font-bold text-black-150 align-middle justify-center">
                  {`${t(`Application number`)}:`}
                </p>
                <p className="text-sm lg:text-base font-bold text-black-150 align-middle justify-center">
                  {`${applicationNo}`}
                </p>
              </div>
            </div>
            <div className="flex flex-1">
              <ParticipantView
                capturedImage={capturedImage}
                setCapturedImage={setCapturedImage}
                setAgreed={setAgreed}
                agreed={agreed}
                currentPage={currentPage}
                abortSignal={abortSignal}
              />
            </div>
          </div>
        ) : (
          <ParticipantView
            capturedImage={capturedImage}
            setCapturedImage={setCapturedImage}
            setAgreed={setAgreed}
            agreed={agreed}
            currentPage={currentPage}
            abortSignal={abortSignal}
          />
        )}
        <CharacterView
          getScreenshotImage={getScreenshotImage}
          characterJoined={characterJoined}
          currentPage={currentPage}
          agreed={agreed}
          setIsCharacterSpeaking={setIsCharacterSpeaking}
          isCharacterSpeaking={isCharacterSpeaking}
          messageSent={messageSent}
          setMessageSent={setMessageSent}
          message={journeyConfig?.voiceoverPolicyDetails}
        />
      </div>
    </div>
  );
};

export default PolicyDetailsPage;
